/**
 * The outline of a content page: its sections, lists and list items as a tree, so the
 * editor shows one part at a time instead of the whole page in nested panels. Paths use
 * the notation of `validate` (`series[2].items[1]`, 1-based), which is also what goes in
 * the address (`?pad=`).
 */
import { byName, hiddenByLayout, isAlphabetical, isRecord, itemSummary, labelFor, nullableInner, pathParts, JsonSchema, ValidationError } from '@/content/schema';

export type OutlineKind = 'root' | 'section' | 'list' | 'item';

export interface OutlineNode {
  /** Path of the part; the page itself is `ROOT_ID`. */
  id: string;
  name: string;
  kind: OutlineKind;
  /** Number of items of a list, or of the one list of a group. */
  count?: number;
  /** Lower-case text of the part's own fields (not of the parts below it), for the search. */
  text: string;
  children?: OutlineNode[];
}

/** v-treeview needs a non-empty key; the page's own fields live at path ''. */
export const ROOT_ID = '$';

export function nodePath(id: string): string {
  return id === ROOT_ID ? '' : id;
}

export function nodeId(path: string): string {
  return path === '' ? ROOT_ID : path;
}

export function childPath(path: string, key: string): string {
  return path ? `${path}.${key}` : key;
}

/** 1-based, like the paths of `validate`. */
export function itemPath(path: string, index: number): string {
  return `${path}[${index + 1}]`;
}

/** The list a list item belongs to, and the item's 0-based index; null for anything else. */
export function parentList(path: string): { list: string; index: number } | null {
  const match = /^(.*)\[(\d+)\]$/.exec(path);
  return match ? { list: match[1], index: Number(match[2]) - 1 } : null;
}

export function getAt(value: unknown, path: string): unknown {
  let current = value;
  for (const part of pathParts(path)) {
    if (typeof part === 'number') current = Array.isArray(current) ? current[part - 1] : undefined;
    else current = isRecord(current) ? current[part] : undefined;
  }
  return current;
}

/** A copy of `value` with `next` at `path`; the parts on the way are copied, the rest is shared. */
export function setAt(value: unknown, path: string, next: unknown): unknown {
  const parts = pathParts(path);
  const set = (current: unknown, depth: number): unknown => {
    if (depth === parts.length) return next;
    const part = parts[depth];
    if (typeof part === 'number') {
      const items = Array.isArray(current) ? [...current] : [];
      items[part - 1] = set(items[part - 1], depth + 1);
      return items;
    }
    const record = isRecord(current) ? { ...current } : {};
    record[part] = set(record[part], depth + 1);
    return record;
  };
  return set(value, 0);
}

/** The schema of the value at `path`; nullable wrappers are unwrapped on the way. */
export function schemaAt(schema: JsonSchema, path: string): JsonSchema | null {
  let current: JsonSchema | null = schema;
  for (const part of pathParts(path)) {
    if (!current) return null;
    current = nullableInner(current) || current;
    current = typeof part === 'number' ? current.items || null : (current.properties || {})[part] || null;
  }
  return current;
}

/** The layout (`slides` or `factchecks`) of the nearest series on the way to `path`, which hides the other layout's fields. */
export function layoutAt(value: unknown, path: string): string {
  let layout = '';
  let current = value;
  if (isRecord(current) && typeof current.layout === 'string') layout = current.layout;
  for (const part of pathParts(path)) {
    current = typeof part === 'number' ? (Array.isArray(current) ? current[part - 1] : undefined) : isRecord(current) ? current[part] : undefined;
    if (isRecord(current) && typeof current.layout === 'string') layout = current.layout;
  }
  return layout;
}

function unwrap(schema: JsonSchema): JsonSchema {
  return nullableInner(schema) || schema;
}

/** A list whose items are groups of fields gets rows and a page per item; lists of plain texts stay inline. */
export function isObjectList(schema: JsonSchema): boolean {
  const inner = unwrap(schema);
  return inner.type === 'array' && Boolean(inner.items) && unwrap(inner.items as JsonSchema).type === 'object';
}

/** Whether a group holds a list of groups somewhere inside, which makes it a part of its own in the outline. */
function holdsObjectList(schema: JsonSchema): boolean {
  const inner = unwrap(schema);
  if (isObjectList(inner)) return true;
  return inner.type === 'object' && Object.values(inner.properties || {}).some(holdsObjectList);
}

/**
 * Whether the group at `key` (a property of the group at `depth`) is a part of its own: every group
 * directly under the page is a section; deeper, only groups that hold a list of groups. A group that
 * can be switched off (nullable) stays in its parent, with its switch.
 */
export function isOwnPart(schema: JsonSchema, depth: number): boolean {
  if (nullableInner(schema)) return false;
  if (isObjectList(schema)) return true;
  return schema.type === 'object' && (depth === 0 || holdsObjectList(schema));
}

function normalise(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** The strings of a value, without those of the parts that have their own node. */
function ownText(schema: JsonSchema, value: unknown, depth: number): string {
  const strings: string[] = [];
  const walk = (current: JsonSchema, currentValue: unknown, currentDepth: number, top: boolean): void => {
    const inner = unwrap(current);
    if (!top && isOwnPart(current, currentDepth)) return;
    if (typeof currentValue === 'string') strings.push(currentValue);
    else if (Array.isArray(currentValue) && inner.items) currentValue.forEach((item) => walk(inner.items as JsonSchema, item, currentDepth + 1, false));
    else if (isRecord(currentValue) && inner.properties)
      for (const [key, property] of Object.entries(inner.properties)) walk(property, currentValue[key], currentDepth + 1, false);
  };
  walk(schema, value, depth, true);
  return strings.join(' ');
}

function objectChildren(schema: JsonSchema, value: unknown, path: string, depth: number, layout: string): OutlineNode[] {
  const inner = unwrap(schema);
  const record = isRecord(value) ? value : {};
  const ownLayout = typeof record.layout === 'string' ? record.layout : layout;
  const nodes: OutlineNode[] = [];
  for (const [key, property] of Object.entries(inner.properties || {})) {
    if (hiddenByLayout(key, ownLayout) || !isOwnPart(property, depth)) continue;
    nodes.push(buildNode(property, record[key], childPath(path, key), labelFor(key), depth + 1, ownLayout));
  }
  return nodes;
}

/**
 * A group whose only part is one list shows that list's items directly (Team › Jan instead of
 * Team › Teamleden › Jan); the group's page shows the list as rows.
 */
function withChildren(node: OutlineNode, children: OutlineNode[]): OutlineNode {
  if (children.length === 1 && children[0].kind === 'list') return { ...node, count: children[0].count, ...(children[0].children?.length ? { children: children[0].children } : {}) };
  return children.length ? { ...node, children } : node;
}

function buildNode(schema: JsonSchema, value: unknown, path: string, name: string, depth: number, layout: string): OutlineNode {
  const inner = unwrap(schema);
  if (isObjectList(inner)) {
    const items = Array.isArray(value) ? value : [];
    const itemSchema = inner.items as JsonSchema;
    const children = items.map((item, index) => {
      const node: OutlineNode = { id: itemPath(path, index), name: itemSummary(item, index), kind: 'item', text: normalise(ownText(itemSchema, item, depth + 1)) };
      return withChildren(node, objectChildren(itemSchema, item, itemPath(path, index), depth + 1, layout));
    });
    if (isAlphabetical(schema)) children.sort((a, b) => byName(a.name, b.name));
    return {
      id: nodeId(path),
      name,
      kind: 'list',
      count: items.length,
      text: normalise(name),
      children,
    };
  }
  const node: OutlineNode = { id: nodeId(path), name, kind: 'section', text: normalise(`${name} ${ownText(inner, value, depth)}`) };
  return withChildren(node, objectChildren(inner, value, path, depth, layout));
}

/** The page's own fields: the properties that are not parts of their own. */
function hasOwnFields(schema: JsonSchema): boolean {
  return Object.values(unwrap(schema).properties || {}).some((property) => !isOwnPart(property, 0));
}

/**
 * The outline of a page: "Algemeen" for the page's own fields (when it has any), then its
 * sections. Empty for a page without parts, which is shown as one form.
 */
export function buildOutline(schema: JsonSchema, value: unknown): OutlineNode[] {
  const sections = objectChildren(schema, value, '', 0, '');
  if (!sections.length) return [];
  const root: OutlineNode[] = hasOwnFields(schema) ? [{ id: ROOT_ID, name: 'Algemeen', kind: 'root', text: normalise(`algemeen ${ownText(schema, value, 0)}`) }] : [];
  return [...root, ...sections];
}

export function flatten(nodes: OutlineNode[]): OutlineNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children || [])]);
}

/** The ids of the nodes on the way to `id`, itself excluded. */
export function ancestors(nodes: OutlineNode[], id: string): string[] {
  const walk = (list: OutlineNode[], trail: string[]): string[] | null => {
    for (const node of list) {
      if (node.id === id) return trail;
      const found = walk(node.children || [], [...trail, node.id]);
      if (found) return found;
    }
    return null;
  };
  return walk(nodes, []) || [];
}

/** The deepest node that holds `path`: where a field or an error lives. */
export function nodeFor(nodes: OutlineNode[], path: string): OutlineNode | null {
  let best: OutlineNode | null = null;
  for (const node of flatten(nodes)) {
    const own = nodePath(node.id);
    const holds = own === '' ? true : path === own || path.startsWith(`${own}.`) || path.startsWith(`${own}[`);
    if (holds && (!best || own.length > nodePath(best.id).length)) best = node;
  }
  return best;
}

/** Search: every word of the query appears in the node's own text (accents and case ignored). */
export function matches(node: OutlineNode, query: string): boolean {
  const words = normalise(query).split(/\s+/).filter(Boolean);
  return words.length > 0 && words.every((word) => node.text.includes(word));
}

export function errorsWithin(errors: ValidationError[], path: string): number {
  return errors.filter((error) => !path || error.path === path || error.path.startsWith(`${path}.`) || error.path.startsWith(`${path}[`)).length;
}

/** Whether anything at or under `path` differs from the saved content. */
export function changedAt(current: unknown, saved: unknown, path: string): boolean {
  return JSON.stringify(getAt(current, path)) !== JSON.stringify(getAt(saved, path));
}

/** Whether one of the page's own fields (those under "Algemeen") differs from the saved content. */
export function ownFieldsChanged(schema: JsonSchema, current: unknown, saved: unknown): boolean {
  return Object.entries(unwrap(schema).properties || {}).some(([key, property]) => !isOwnPart(property, 0) && changedAt(current, saved, key));
}
